import { Flex, Spacer } from "@chakra-ui/react";
import { MdOutlineEdit } from "react-icons/md";
import { FormattedMessage, useIntl } from "react-intl";

import { TenantRole, UserListOrderByEnum, UserRead } from "@nens/rana/api-v1";
import { AddIcon, AddUserIcon, RanaButton, RanaHeading, RanaTable, TrashIcon } from "@nens/ranalib";

import { TableEmptyUserOrInvite } from "@/components/table-empty-user-or-invite";
import { useRanaSearchState } from "@/hooks/router/rana-search-state";
import { useRanaTableData } from "@/hooks/router/use-rana-table-data";
import { useTenantActionAllowed } from "@/hooks/use-action-allowed-in-tenant";
import { Profile } from "@/lib/profile";
import { useTenantUserListQuery } from "@/queries/query-tenant-user";
import { userRoleString } from "@/translations";
import { getCompleteName } from "@/utils/get-complete-name";

import { UserInviteList } from "../invite-user";

import { TenantUserActivateModal } from "./components/tenant-user-activate-modal";
import { TenantUserDeactivateModal } from "./components/tenant-user-deactivate-modal";
import { TenantUserEditableRoleModal } from "./components/tenant-user-editable-role-modal";
import { TenantUserInviteModal } from "./components/tenant-user-invite-modal";
import { useTenantUserModal } from "./hooks/use-tenant-user-list";

export function TenantUserList({ onUserClick }: { onUserClick?: (user: UserRead) => void }) {
  const [showActive, setShowActive] = useRanaSearchState("tuActive", true);
  const [showInActive, setShowInActive] = useRanaSearchState("tuInactive", false);
  const [showRoles, setShowRoles] = useRanaSearchState<TenantRole[]>("tuRoles", [], ["string"]);
  const {
    page,
    offset,
    limit,
    setPageSize,
    setPage,
    orderings,
    orderingString,
    setOrdering,
    searchString,
    setSearchString,
  } = useRanaTableData({ keyAddon: "tenantUser", initialPageSize: 5 });

  const allRoles = Object.values(TenantRole);
  const selectedRoles = allRoles.filter((role) => showRoles.includes(role));
  const shouldFilterByRole = selectedRoles.length > 0 && selectedRoles.length < allRoles.length;
  const usersListQuery = useTenantUserListQuery({
    offset,
    limit,
    orderBy: (orderingString as UserListOrderByEnum) || null,
    search: searchString && searchString.length > 2 ? searchString : undefined,
    active: showActive !== showInActive ? showActive : undefined,
    role: shouldFilterByRole ? selectedRoles : undefined,
  });
  const usersList = usersListQuery?.data?.items ?? [];

  const intl = useIntl();
  const {
    modal,
    openChangeRoleModal,
    openDeactivateModal,
    openActivateModal,
    openInviteModal,
    closeModal,
  } = useTenantUserModal();

  const { canManageTenant } = useTenantActionAllowed();

  const findUsersByIds = (rowIds: string[]) =>
    rowIds
      .map((rowId) => usersList.find(({ id }) => rowId === id))
      .filter((user): user is UserRead => !!user);

  const inviteUsersClick = canManageTenant ? openInviteModal : undefined;

  // render checkboxes for all user roles defined by backend
  const roleCheckboxes = allRoles.map((role) => ({
    label: userRoleString(intl, role),
    checked: showRoles.includes(role),
    setChecked: (checked: boolean) => {
      if (checked === showRoles.includes(role)) return;

      setPage(1);
      setShowRoles((previousRoles) =>
        checked
          ? [...previousRoles, role]
          : previousRoles.filter((selectedRole) => selectedRole !== role)
      );
    },
  }));

  return (
    <>
      {canManageTenant && modal && (
        <>
          {modal.modal === "change-role" && (
            <TenantUserEditableRoleModal user={modal.currentModalUser} onClose={closeModal} />
          )}
          {modal.modal === "deactivate" && (
            <TenantUserDeactivateModal users={modal.currentModalUsers} onClose={closeModal} />
          )}
          {modal.modal === "activate" && (
            <TenantUserActivateModal users={modal.currentModalUsers} onClose={closeModal} />
          )}
          {modal.modal === "invite" && <TenantUserInviteModal onClose={closeModal} />}
        </>
      )}

      <Flex direction="column" gap={2}>
        <Flex flexWrap="wrap" alignItems="center">
          <RanaHeading type="h4">
            <FormattedMessage id="heading.invited_users" defaultMessage="Invited users" />{" "}
          </RanaHeading>
          <Spacer />
          {canManageTenant ? (
            <RanaButton onClick={openInviteModal} leftIcon={<AddUserIcon />} variant="primary">
              <FormattedMessage id="button.invite" defaultMessage="Invite users" />
            </RanaButton>
          ) : null}
        </Flex>
        <UserInviteList openInviteModal={inviteUsersClick} />

        <RanaHeading type="h4">
          <FormattedMessage id="heading.users" defaultMessage="Users" />
        </RanaHeading>

        <RanaTable
          setOrdering={setOrdering}
          ordering={orderings}
          isSkeleton={usersListQuery.isLoading}
          emptyElement={<TableEmptyUserOrInvite onLinkCLick={inviteUsersClick} />}
          columnDefinitions={[
            {
              title: "",
              width: "table_cell_xxxs",
            },
            {
              title: intl.formatMessage({ id: "column.name", defaultMessage: "Name" }),
              orderingField: "given_name",
              width: "table_cell_xs",
            },
            {
              title: intl.formatMessage({ id: "column.email", defaultMessage: "Email" }),
              orderingField: "email",
              width: "table_cell",
            },
            {
              title: intl.formatMessage({
                id: "column.tenant_role",
                defaultMessage: "Organisation role",
              }),
              width: "table_cell_xs",
            },
            {
              title: intl.formatMessage({
                id: "column.status",
                defaultMessage: "Status",
              }),
              width: "table_cell_xxs",
            },
          ]}
          tableDataIds={usersList.map((user) => user.id)}
          hasRowActions={canManageTenant}
          tableActions={
            canManageTenant
              ? [
                  {
                    label: intl.formatMessage({
                      id: "menu.deactivate_selected_users",
                      defaultMessage: "Deactivate selected users",
                    }),
                    action: (rowIds) => {
                      const selectedActiveUsers = findUsersByIds(rowIds).filter(
                        ({ active }) => active
                      );
                      openDeactivateModal(selectedActiveUsers);
                    },
                    getDisabledText: (rowIds) =>
                      findUsersByIds(rowIds).some(({ active }) => !active)
                        ? "Inactive user selected"
                        : undefined,
                    icon: <TrashIcon />,
                  },
                  {
                    label: intl.formatMessage({
                      id: "menu.activate_selected_users",
                      defaultMessage: "Activate selected users",
                    }),
                    action: (rowIds) => {
                      const selectedDisabledUsers = findUsersByIds(rowIds).filter(
                        ({ active }) => !active
                      );
                      openActivateModal(selectedDisabledUsers);
                    },
                    getDisabledText: (rowIds) =>
                      findUsersByIds(rowIds).some(({ active }) => active)
                        ? "Active user selected"
                        : undefined,
                    icon: <AddIcon />,
                  },
                ]
              : []
          }
        >
          <RanaTable.Filters
            search={{
              placeholder: intl.formatMessage({
                id: "placeholder.find_user",
                defaultMessage: "Find user",
              }),
              searchString,
              setSearchString,
            }}
            checkboxGroups={[
              {
                label: intl.formatMessage({ id: "filter.role", defaultMessage: "Role" }),
                checkboxes: roleCheckboxes,
              },
              {
                label: intl.formatMessage({ id: "filter.status", defaultMessage: "Status" }),
                checkboxes: [
                  {
                    label: intl.formatMessage({
                      id: "label.active",
                      defaultMessage: "Active",
                    }),
                    checked: showActive,
                    setChecked: (checked) => {
                      setPage(1);
                      setShowActive(checked);
                    },
                  },
                  {
                    label: intl.formatMessage({
                      id: "label.inactive",
                      defaultMessage: "Inactive",
                    }),
                    checked: showInActive,
                    setChecked: (checked) => {
                      setPage(1);
                      setShowInActive(checked);
                    },
                  },
                ],
              },
            ]}
          />
          <RanaTable.Container minH="fit">
            <RanaTable.Header />
            <RanaTable.Body>
              {usersList.map((user) => (
                <RanaTable.Row
                  key={user.id}
                  rowId={user.id}
                  w="full"
                  m={0}
                  onClick={() => onUserClick?.(user)}
                  rowActions={
                    canManageTenant
                      ? [
                          {
                            label: "Change user role",
                            action: () => openChangeRoleModal(user),
                            icon: <MdOutlineEdit />,
                          },
                          user.active
                            ? {
                                label: intl.formatMessage({
                                  id: "menu.deactivate_user",
                                  defaultMessage: "Deactivate user account",
                                }),
                                action: () => openDeactivateModal([user]),
                                icon: <TrashIcon />,
                              }
                            : {
                                label: intl.formatMessage({
                                  id: "menu.activate_user",
                                  defaultMessage: "Activate user account",
                                }),
                                action: () => openActivateModal([user]),
                                icon: <AddIcon />,
                              },
                        ]
                      : []
                  }
                >
                  <RanaTable.Cell>
                    <Profile inTable user={user} />
                  </RanaTable.Cell>
                  <RanaTable.Cell>{getCompleteName(user)}</RanaTable.Cell>
                  <RanaTable.Cell>{user.email}</RanaTable.Cell>
                  <RanaTable.Cell>{user.role && userRoleString(intl, user.role)}</RanaTable.Cell>
                  <RanaTable.Cell fontWeight="300">
                    {user.active
                      ? intl.formatMessage({ id: "user.active", defaultMessage: "Active" })
                      : intl.formatMessage({ id: "user.inactive", defaultMessage: "Inactive" })}
                  </RanaTable.Cell>
                </RanaTable.Row>
              ))}
            </RanaTable.Body>
          </RanaTable.Container>
        </RanaTable>
        <RanaTable.Pagination
          page={page}
          numPages={Math.ceil((usersListQuery.data?.total ?? 0) / limit)}
          limit={limit}
          setPageSize={setPageSize}
          setPage={setPage}
        />
      </Flex>
    </>
  );
}
